
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import MFAVerification from '../components/MFAVerification';
import SetNewPasswordForm from '../components/SetNewPasswordForm';
import MFASetupForm from '../components/MFASetupForm';
import ForgotPasswordForm from '../components/ForgotPasswordForm';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AuthContext);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSessionToken, setMfaSessionToken] = useState('');
  const [mfaUsername, setMfaUsername] = useState('');
  
  // MFA setup required state (for users who need to configure MFA)
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaSetupSession, setMfaSetupSession] = useState('');
  const [mfaSetupUsername, setMfaSetupUsername] = useState('');
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // New password required state (for first login with temp password)
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [newPasswordSession, setNewPasswordSession] = useState('');
  const [newPasswordUsername, setNewPasswordUsername] = useState('');
  
  // Redirect if already logged in
  useEffect(() => {
    if (auth.user) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [auth.user, location.state, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const result = await auth.login(username, password);
      
      if (result.success) {
        // Navigate to the intended page or dashboard
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      } else if (result.mfa_required) {
        // MFA is required, show MFA verification form
        setMfaRequired(true);
        setMfaSessionToken(result.session_token);
        setMfaUsername(result.username || username);
      } else if (result.mfa_setup_required) {
        // MFA setup is required (user needs to configure authenticator)
        setMfaSetupRequired(true);
        setMfaSetupSession(result.session_token);
        setMfaSetupUsername(result.username || username);
      } else if (result.new_password_required) {
        // New password required (first login with temp password)
        setNewPasswordRequired(true);
        setNewPasswordSession(result.session_token);
        setNewPasswordUsername(result.username || username);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      setError(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleMfaVerified = () => {
    // MFA verified successfully, user is now logged in
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  };
  
  const handleMfaCancel = () => {
    // User cancelled MFA, go back to login form
    setMfaRequired(false);
    setMfaSessionToken('');
    setMfaUsername('');
    setPassword(''); // Clear password for security
  };
  
  const handleNewPasswordSet = async (newPassword) => {
    // Call the setNewPassword endpoint
    const result = await auth.setNewPassword(newPasswordUsername, newPasswordSession, newPassword);
    
    if (result.mfa_required) {
      // Password changed but MFA is now required
      setNewPasswordRequired(false);
      setMfaRequired(true);
      setMfaSessionToken(result.session_token);
      setMfaUsername(newPasswordUsername);
    } else if (result.mfa_setup_required) {
      // Password changed but MFA setup is required
      setNewPasswordRequired(false);
      setMfaSetupRequired(true);
      setMfaSetupSession(result.session_token);
      setMfaSetupUsername(newPasswordUsername);
    } else if (result.success) {
      // Password changed and logged in successfully
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  };
  
  const handleNewPasswordCancel = () => {
    // User cancelled, go back to login form
    setNewPasswordRequired(false);
    setNewPasswordSession('');
    setNewPasswordUsername('');
    setPassword(''); // Clear password for security
  };
  
  const handleMfaSetupComplete = async (result) => {
    // MFA setup complete, user is now logged in
    // We need to fetch user data and update the auth context
    try {
      const userData = await auth.completeLoginWithTokens(result.access_token);
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      // If completeLoginWithTokens doesn't exist, just navigate
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  };
  
  const handleMfaSetupCancel = () => {
    // User cancelled MFA setup, go back to login form
    setMfaSetupRequired(false);
    setMfaSetupSession('');
    setMfaSetupUsername('');
    setPassword(''); // Clear password for security
  };
  
  // Show forgot password form
  if (showForgotPassword) {
    return (
      <ForgotPasswordForm
        onCancel={() => setShowForgotPassword(false)}
        onPasswordReset={() => setShowForgotPassword(false)}
      />
    );
  }
  
  // Show MFA setup form if required (user needs to configure authenticator)
  if (mfaSetupRequired) {
    return (
      <MFASetupForm
        username={mfaSetupUsername}
        session={mfaSetupSession}
        onSetupComplete={handleMfaSetupComplete}
        onCancel={handleMfaSetupCancel}
      />
    );
  }
  
  // Show new password form if required (first login with temp password)
  if (newPasswordRequired) {
    return (
      <SetNewPasswordForm
        username={newPasswordUsername}
        session={newPasswordSession}
        onPasswordSet={handleNewPasswordSet}
        onCancel={handleNewPasswordCancel}
      />
    );
  }
  
  // Show MFA verification form if MFA is required
  if (mfaRequired) {
    return (
      <MFAVerification
        sessionToken={mfaSessionToken}
        username={mfaUsername}
        onVerified={handleMfaVerified}
        onCancel={handleMfaCancel}
      />
    );
  }
  
  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Rebalancer 2.0</h2>
        {error && <div className="alert alert-danger">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="username" className="form-label">Username</label>
            <input
              type="text"
              className="form-control"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              className="form-control"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <div className="mt-2 text-center">
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => setShowForgotPassword(true)}
          >
            Forgot Password?
          </button>
        </div>
        <div className="mt-2 text-center">
          <small className="text-muted">
            (If you need an account or help, contact Greg.)
          </small>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
